import React from 'react';
import { createPortal } from 'react-dom';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, FormFeedback
} from 'reactstrap';
import { SponsorData, SponsorTypeData } from '../types';
import ImageCropper from '../../../../components/common/ImageCropper/ImageCropper';

interface SponsorModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (sponsor: SponsorData) => void;
  isLoading: boolean;
  sponsor?: SponsorData;
  sponsorTypes: SponsorTypeData[];
}

const SponsorModal: React.FC<SponsorModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  isLoading,
  sponsor,
  sponsorTypes
}) => {
  const [previewImage, setPreviewImage] = React.useState<string | null>(null);
  const previewUrlRef = React.useRef<string | null>(null);

  const [errors, setErrors] = React.useState<{ [k: string]: string }>({});
  const [formData, setFormData] = React.useState<{
    sponsorTypeId: string;
    name: string;
    link: string;
    thumb: File | null;
    thumbImageKey?: string;      // ← filename for backend match
  }>({
    sponsorTypeId: '',
    name: '',
    link: '',
    thumb: null,
    thumbImageKey: undefined
  });

  const [isCropperOpen, setIsCropperOpen] = React.useState(false);
  const [fileToCrop, setFileToCrop] = React.useState<File | null>(null);

  // Init form on open/edit
  React.useEffect(() => {
    // cleanup previous object URL
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  if (sponsor) {
    setFormData({
      sponsorTypeId: String(sponsor.sponsorTypeId || ''),
      name: sponsor.name || '',
      link: sponsor.link || '',
      thumb: sponsor.thumb instanceof File ? sponsor.thumb : null,
      thumbImageKey: sponsor.thumbImageKey || undefined,
    });

    // ✅ Handle all possible preview sources safely
    if (sponsor.thumbImageUrl) {
      setPreviewImage(sponsor.thumbImageUrl);
    } else if (typeof sponsor.thumb === 'string') {
      setPreviewImage(sponsor.thumb);
    } else if (
      sponsor.thumb &&
      typeof sponsor.thumb === 'object' &&
      'size' in sponsor.thumb &&
      'type' in sponsor.thumb
    ) {
      const file = sponsor.thumb as Blob;
      const url = URL.createObjectURL(file);
      previewUrlRef.current = url;
      setPreviewImage(url);
    } else {
      setPreviewImage(null);
    }
  } else {
    // reset for new sponsor
    setFormData({
      sponsorTypeId: '',
      name: '',
      link: '',
      thumb: null,
      thumbImageKey: undefined,
    });
    setPreviewImage(null);
  }

  setErrors({});
}, [sponsor, isOpen]);



  React.useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  const isValidUrl = (url: string) => {
    if (!url) return true;
    try { new URL(url); return true; } catch { return false; }
  };

  const validateForm = () => {
    const next: { [k: string]: string } = {};
    if (!formData.sponsorTypeId) next.sponsorTypeId = 'Sponsor type is required';
    if (!formData.name.trim()) next.name = 'Sponsor name is required';
    if (formData.link && !isValidUrl(formData.link)) next.link = 'Please enter a valid URL';
    const hasExistingImage = !!(
      formData.thumb ||
      sponsor?.thumbImageUrl ||
      (typeof sponsor?.thumb === 'string' && sponsor.thumb) ||
      sponsor?.thumbImageKey
    );
    if (!hasExistingImage) {
      next.thumb = 'Sponsor image required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      // reset to existing url if any
      setFormData(prev => ({ ...prev, thumb: null, thumbImageKey: undefined }));
      setPreviewImage(sponsor?.thumbImageUrl || (typeof sponsor?.thumb === 'string' ? sponsor?.thumb : null));
      return;
    }

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, thumb: 'Only image files are allowed' }));
      return;
    }

    const max5MB = 5 * 1024 * 1024;
    if (file.size > max5MB) {
      setErrors(prev => ({ ...prev, thumb: 'Max file size is 5MB' }));
      return;
    }

    // cleanup old preview URL
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    // create preview and open cropper
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreviewImage(url);

    setFormData(prev => ({
      ...prev,
      thumb: file,
      thumbImageKey: file.name,
    }));
    setErrors(prev => {
      const newErr = { ...prev };
      delete newErr.thumb;
      return newErr;
    });

    setFileToCrop(file);
    setIsCropperOpen(true);
  };


  const buildSponsor = (): SponsorData => ({
    sponsorId: sponsor?.sponsorId || `sponsor_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    id: sponsor?.id || `sponsor_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    sponsorTypeId: formData.sponsorTypeId, // Keep as string to match API response
    name: formData.name.trim(),
    link: formData.link.trim() || undefined,
    thumb: formData.thumb,                 // ← FILE (or null if user didn't change)
    thumbImageKey: formData.thumbImageKey, // ← filename
    thumbImageUrl: sponsor?.thumbImageUrl || null, // keep existing url for preview
    isActive: sponsor?.isActive ?? true,
    sponsorType: sponsor?.sponsorType,     // passthrough if present
    eventSlug: '', // Will be set by parent component
    createdAt: sponsor?.createdAt || '',
    updatedAt: sponsor?.updatedAt || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit(buildSponsor());
  };

  const handleFormSubmit = () => {
    if (!validateForm()) return;
    onSubmit(buildSponsor());
  };

  return (
    <Modal modalClassName="quick-v2-glass-modal" isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {sponsor ? 'Edit Sponsor' : 'Add New Sponsor'}
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="sponsorType">Sponsor Type *</Label>
            <Input
              id="sponsorType"
              type="select"
              value={formData.sponsorTypeId}
              onChange={(e) => setFormData(prev => ({ ...prev, sponsorTypeId: e.target.value }))}
              invalid={!!errors.sponsorTypeId}
            >
              <option value="">Select Sponsor Type</option>
              {sponsorTypes.map(t => (
                <option key={t.sponsorTypeId} value={t.sponsorTypeId}>{t.title}</option>
              ))}
            </Input>
            {errors.sponsorTypeId && <FormFeedback>{errors.sponsorTypeId}</FormFeedback>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="sponsorName">Sponsor Name *</Label>
            <Input
              id="sponsorName"
              type="text"
              placeholder="Enter sponsor name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              invalid={!!errors.name}
            />
            {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="sponsorLink">Website URL</Label>
            <Input
              id="sponsorLink"
              type="url"
              placeholder="https://example.com"
              value={formData.link}
              onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
              invalid={!!errors.link}
            />
            {errors.link && <FormFeedback>{errors.link}</FormFeedback>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="sponsorImage">Sponsor Logo</Label>
            {previewImage && (
              <div className="mb-3">
                <img
                  src={previewImage}
                  alt="Sponsor logo preview"
                  style={{
                    width: 120, height: 120, objectFit: 'cover',
                    border: '1px solid #ddd', borderRadius: 4
                  }}
                />
              </div>
            )}
            <style>{`
              .sponsor-file-input {
                display: flex !important;
                align-items: center !important;
                height: 46px !important;
                padding: 4px 8px !important;
              }
              .sponsor-file-input::-webkit-file-upload-button,
              .sponsor-file-input::file-selector-button {
                margin: 0 12px 0 0 !important;
                border-radius: 6px !important;
                height: 34px !important;
                padding: 0 14px !important;
                display: inline-flex !important;
                align-items: center !important;
                justify-content: center !important;
                border: none !important;
                background: #ffffff !important;
                color: #222222 !important;
                font-weight: 500 !important;
                cursor: pointer !important;
              }
            `}</style>
            <Input
              id="sponsorImage"
              type="file"
              accept="image/*"
              className="sponsor-file-input"
              onChange={handleImageChange}
              invalid={!!errors.thumb}
            />
            {errors.thumb && <FormFeedback>{errors.thumb}</FormFeedback>}
            <small className="form-text text-muted">
              Recommended size: 200×200. Max 5MB.
            </small>
          </FormGroup>

          {/* === Cropper Modal === */}
          {isCropperOpen && fileToCrop && createPortal(
            <div className="cropper-modal-overlay">
              <div className="cropper-modal-card">
                <ImageCropper
                  imageFile={fileToCrop}
                  aspect={1}
                  onCancel={() => {
                    setIsCropperOpen(false);
                    setFileToCrop(null);
                  }}
                  onCropComplete={(croppedImg) => {
                    const url = URL.createObjectURL(croppedImg);
                    if (previewUrlRef.current)
                      URL.revokeObjectURL(previewUrlRef.current);
                    previewUrlRef.current = url;

                    setPreviewImage(url);
                    setFormData(prev => ({
                      ...prev,
                      thumb: croppedImg,
                      thumbImageKey: croppedImg.name,
                    }));
                    setErrors(prev => {
                      const newErr = { ...prev };
                      delete newErr.thumb;
                      return newErr;
                    });

                    setIsCropperOpen(false);
                    setFileToCrop(null);
                  }}
                />
              </div>
            </div>,
            document.body
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-secondary" onClick={toggle} disabled={isLoading}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={handleFormSubmit} disabled={isLoading}>
          {isLoading ? 'Saving...' : (sponsor ? 'Update Sponsor' : 'Add Sponsor')}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default SponsorModal;
